import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Spinner, Icon, useAuth } from '@iq/react-components';

import { getMembers } from '../../bundles/auth';
import RoleSelect from '../RoleSelect';
import { LIMITED_PERMISSION_ENTITIES } from '../../constants';

const normalizeEntityName = (entity) => {
  if (entity.includes('Docs')) {
    return entity
      .split(/(?=[A-Z])/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  return entity.charAt(0).toUpperCase() + entity.slice(1);
};

const PermissionLegend = ({ selectedMember }) => {
  const { realm } = useAuth();
  const orgMembers = useSelector((state) => getMembers(state, `org/${realm}`));
  const [selectedRole, setSelectedRole] = useState(selectedMember);

  const SYSTEM_PERMISSIONS = new Set(['users', 'instructionals', 'systemMessages', 'project']);

  useEffect(() => {
    if (!selectedMember && orgMembers) {
      setSelectedRole(orgMembers.find((member) => member.type === 'Viewer'));
    }
  }, [selectedMember, JSON.stringify(orgMembers)]);

  const onRoleSelect = useCallback(
    (memberType) => setSelectedRole(orgMembers.find((member) => member.type === memberType)),
    [orgMembers]
  );

  const permissionsByType = useMemo(
    () =>
      (selectedRole?.permissions || []).reduce((acc, permission) => {
        const bits = permission.split('/');
        if (!SYSTEM_PERMISSIONS.has(bits[0])) {
          return {
            ...acc,
            [bits[0]]: [bits[1], ...(acc[bits[0]] || [])],
          };
        }
        return acc;
      }, {}),
    [selectedRole]
  );

  if (!orgMembers || !selectedRole) {
    return <Spinner data-testid="spinner" />;
  }

  return (
    <div className={'permission-legend-component'}>
      {!selectedMember && (
        <RoleSelect
          isMulti={false}
          value={selectedRole.type}
          realm={realm}
          onChange={onRoleSelect}
          hideAllRoles
        />
      )}

      <div className="permissions-table">
        <table>
          <thead>
            <tr>
              <th />
              <th>Limited</th>
              <th>Read</th>
              <th>Write</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(permissionsByType)
              .toSorted(([entityA], [entityB]) => entityA.localeCompare(entityB))
              .map(([entity, permissionList]) => (
                <tr key={entity}>
                  <td>{normalizeEntityName(entity)}</td>
                  <td>
                    <center>
                      {permissionList.includes(`${LIMITED_PERMISSION_ENTITIES[entity]?.[0]}Limited`)
                        ? 'Yes'
                        : Object.keys(LIMITED_PERMISSION_ENTITIES).includes(entity)
                          ? 'No'
                          : ''}
                    </center>
                  </td>
                  <td>
                    <Icon
                      data-testid={
                        permissionList.includes('Read') || permissionList.includes('ReadLimited')
                          ? 'check-icon'
                          : 'blank-icon'
                      }
                      icon={
                        permissionList.includes('Read') || permissionList.includes('ReadLimited')
                          ? 'he-check'
                          : 'blank'
                      }
                    />
                  </td>
                  <td>
                    <Icon
                      data-testid={
                        permissionList.includes('Write') || permissionList.includes('WriteLimited')
                          ? 'check-icon'
                          : 'blank-icon'
                      }
                      icon={
                        permissionList.includes('Write') || permissionList.includes('WriteLimited')
                          ? 'he-check'
                          : 'blank'
                      }
                    />
                  </td>
                  <td>
                    <Icon
                      data-testid={
                        permissionList.includes('Delete') ||
                        permissionList.includes('DeleteLimited')
                          ? 'check-icon'
                          : 'blank-icon'
                      }
                      icon={
                        permissionList.includes('Delete') ||
                        permissionList.includes('DeleteLimited')
                          ? 'he-check'
                          : 'blank'
                      }
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PermissionLegend;
